import styled, { keyframes } from 'styled-components'

import theme from 'app/match/applicationReportPDF/assets/theme'

// Animations
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

// TypeScript interfaces
interface CheckboxProps {
  completed: boolean
  pending: boolean
}

interface StatusTextProps {
  success: boolean
}

// Main Container
export const OnboardingContainer = styled.div`
  max-width: ${theme.width[35]}px;
  margin: 0 auto;
  background-color: ${theme.colors.white};

  @media (max-width: ${theme.width[23]}px) {
    margin-top: ${theme.space[5]}px;
  }
`

// Header Section
export const HeaderContainer = styled.div`
  text-align: center;
  margin-bottom: ${theme.space[8]}px;
`

export const WelcomeTitle = styled.h1`
  font-size: ${theme.baseSpace * 4}px;
  font-weight: 600;
  margin-bottom: ${theme.space[2]}px;
`

export const WelcomeDescription = styled.p`
  color: ${theme.colors.slate500};
  max-width: ${theme.width[34]}px;
  margin: 0 auto;
`

// Organization & Plan Section
export const OrganizationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const InfoLabel = styled.p`
  font-size: ${theme.fontSizes.pRegular14}px;
  color: ${theme.colors.slate500};
  margin-bottom: ${theme.space[1]}px;
`

export const InfoValue = styled.p`
  font-weight: 500;
`

// Step Card Container
export const StepCardContainer = styled.div`
  margin-bottom: ${theme.space[5]}px;

  @media (max-width: ${theme.width[23]}px) {
    margin-bottom: ${theme.space[6]}px;
  }
`

export const StepCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[5]}px;

  @media (max-width: ${theme.width[23]}px) {
    gap: ${theme.space[4]}px;
  }
`

// Checkbox Styles
export const CheckboxContainer = styled.div<CheckboxProps>`
  width: ${theme.space[8]}px;
  height: ${theme.space[8]}px;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      props.completed ? theme.colors.emerald500 : theme.colors.slate300};
  background-color: ${(props) =>
    props.completed ? theme.colors.emerald500 : theme.colors.slate100};
  color: ${(props) => (props.completed ? 'white' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${(props) => (props.pending ? 0.5 : 1)};
  transition: all 0.2s ease;
`

export const LoadingSpinner = styled.div`
  width: ${theme.space[5]}px;
  height: ${theme.space[5]}px;
  border: 2px solid ${theme.colors.white};
  border-top: 2px solid ${theme.colors.transparent};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

// Content Container
export const ContentContainer = styled.div`
  flex: 1;
`

// Alert Styles
export const AlertContainer = styled.div`
  background-color: ${theme.colors.amber100};
  border: 1px solid ${theme.colors.amber500};
  border-radius: ${theme.radii[12]}px;
  padding: ${theme.space[5]}px;
  margin-bottom: ${theme.space[4]}px;
`

export const AlertContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.space[4]}px;
`

export const AlertTextContainer = styled.div`
  flex: 1;
`

export const AlertTitle = styled.h4`
  font-size: ${theme.fontSizes.pRegular14};
  font-weight: 600;
  color: ${theme.colors.amber800};
  margin-bottom: ${theme.space[1]}px;
`

export const AlertDescription = styled.p`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.amber800};
  margin-bottom: 0px;
`

// Title and Description Section
export const TitleDescriptionContainer = styled.div`
  display: flex;
  gap: ${theme.space[4]}px;
  align-items: flex-start;
  margin-bottom: ${theme.space[4]}px;

  @media (max-width: ${theme.width[23]}px) {
    flex-direction: column;
    gap: ${theme.space[5]}px;
  }
`

export const TextContainer = styled.div`
  flex: 1;
`

export const StepTitle = styled.h3`
  font-weight: 500;
  margin-bottom: ${theme.space[1]}px;
  font-size: ${theme.fontSizes.pLarge16};
`

export const StepDescription = styled.p`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.slate500};
  margin-bottom: 0px;
`

// Video Button
export const VideoButton = styled.div<{ $thumbnailUrl?: string }>`
  width: ${theme.width[36]}px;
  height: ${theme.height[19]}px;
  background-color: ${theme.colors.slate100};
  background-image: ${({ $thumbnailUrl }) =>
    $thumbnailUrl ? `url(${$thumbnailUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  border: 1px solid ${theme.colors.slate200};
  border-radius: ${theme.radii[12]}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;

  /* Play button overlay */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${theme.space[8]}px;
    height: ${theme.space[8]}px;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    display: ${({ $thumbnailUrl }) => ($thumbnailUrl ? 'block' : 'none')};
  }

  /* Play triangle */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 52%;
    transform: translate(-50%, -50%);
    width: 0;
    height: 0;
    border-left: ${theme.space[2]}px solid white;
    border-top: ${theme.radii[6]}px solid transparent;
    border-bottom: ${theme.radii[6]}px solid transparent;
    display: ${({ $thumbnailUrl }) => ($thumbnailUrl ? 'block' : 'none')};
  }

  &:hover {
    background-color: ${({ $thumbnailUrl }) =>
      $thumbnailUrl ? theme.colors.transparent : theme.colors.slate200};

    &::before {
      background-color: rgba(0, 0, 0, 0.8);
    }

    &::after {
      border-left-color: ${theme.colors.lightGray};
    }
  }

  @media (max-width: ${theme.width[23]}px) {
    width: 100%;
    height: ${theme.height[20]}px;
  }
`

// Additional Content
export const AdditionalContentContainer = styled.div`
  margin-bottom: ${theme.space[4]}px;
`

// Status Section
export const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]}px;
  margin-bottom: ${theme.space[2]}px;
`

export const StatusText = styled.span<StatusTextProps>`
  color: ${(props) =>
    props.success ? theme.colors.emerald500 : theme.colors.slate500};
  font-size: ${theme.fontSizes.pRegular14};
  font-weight: 500;
`

// URL Display Box
export const UrlDisplayContainer = styled.div`
  background-color: ${theme.colors.slate100};
  border: 1px solid ${theme.colors.slate300};
  border-radius: ${theme.radii[11]}px;
  padding: ${theme.space[4]}px;
  margin-bottom: ${theme.space[4]}px;
  margin-top: ${theme.space[4]}px;
  font-family: monospace;
`

export const UrlDisplayContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const UrlText = styled.span`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.slate700};
  word-break: break-all;
  flex: 1;
  margin-right: ${theme.space[3]}px;
`

// Status Display Box
export const StatusDisplayContainer = styled.div`
  background-color: ${theme.colors.slate50};
  border: 1px solid ${theme.colors.blueGray200};
  border-radius: ${theme.radii[11]}px;
  padding: ${theme.space[4]}px;
  margin-bottom: ${theme.space[4]}px;
`

export const StatusDisplayContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]}px;
`

export const StatusDisplayLabel = styled.span`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.slate700};
  font-weight: 500;
`

export const StatusDisplayValue = styled.span`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.slate500};
`

// Action Button Container
export const ActionButtonContainer = styled.div`
  margin-right: ${theme.space[3]}px;
  margin-top: ${theme.space[3]}px;
`

// Property Uploader Selection
export const PropertyUploaderContainer = styled.div`
  margin-bottom: ${theme.space[4]}px;
`

export const PropertyUploaderLabel = styled.p`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.slate700};
  margin-bottom: ${theme.space[3]}px;
  font-weight: 500;
`

export const PropertyUploaderSelectContainer = styled.div`
  width: ${theme.width[33]}px;
  height: ${theme.height[21]}px;

  @media (max-width: ${theme.width[23]}px) {
    width: 100%;
    max-width: ${theme.width[32]}px;
  }

  /* Handle text overflow in select options */
  select,
  .select-dropdown,
  .select-option {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Ensure dropdown content fits within container */
  .select-wrapper {
    width: 100%;
    max-width: 100%;
  }
`

// Loading State
export const LoadingContainer = styled.div`
  padding: ${theme.space[13]}px;
  text-align: center;
`

export const LoadingSpinnerLarge = styled.div`
  width: ${theme.space[13]}px;
  height: ${theme.space[13]}px;
  border: 4px solid ${theme.colors.slate100};
  border-top: 4px solid ${theme.colors.blue500};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto ${theme.space[5]}px;
`

// Error State
export const ErrorContainer = styled.div`
  padding: ${theme.space[13]}px;
`

export const ErrorAlert = styled.div`
  margin-top: ${theme.space[5]}px;
`

// Setup Call Section
export const SetupCallContainer = styled.div``

export const SetupCallDescription = styled.p`
  font-size: ${theme.fontSizes.pRegular14};
  color: ${theme.colors.blue800};
  margin-bottom: ${theme.space[5]}px;
`

export const SetupCallButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]}px;
`
